import { useState, type FunctionComponent } from "react";
import { InfoCircleOutlined, LinkOutlined, ReloadOutlined } from "@ant-design/icons";
import { Badge, Button, message, Space, Tooltip, Typography } from "antd";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { productClientActions } from "@fitness-app/app-store";
import { type InvoiceKsefMetadata, type Order } from "@fitness-app/data-models/entities/Order";

import { useAppDispatch } from "~/store/initializeStore";

interface OwnProps {
  invoice: Order["connectedInvoice"];
  withId?: boolean;
  hideError?: boolean;
  /** Pass both ids to enable the on-demand KSeF status refresh next to the badge. */
  orderId?: string;
  productId?: string;
  onKsefRefreshed?: () => void;
}

type Props = OwnProps;

const getKsefBadgeStatus = (
  status: string | null | undefined,
): "success" | "processing" | "default" | "error" | "warning" => {
  switch (status) {
    case "ok":
    case "demo_ok":
      return "success";
    case "processing":
    case "demo_processing":
      return "processing";
    case "send_error":
    case "server_error":
    case "demo_send_error":
    case "demo_server_error":
      return "error";
    case "not_connected":
    case "demo_not_connected":
      return "warning";
    default:
      return "default";
  }
};

/**
 * Fakturownia supplies the KSeF document URL, so it is third-party data — only accept a
 * plain https link before turning it into an href.
 */
const getKsefLink = (ksef: InvoiceKsefMetadata | null | undefined): string | null => {
  // Check both candidates rather than falling back only on null/undefined — an empty or
  // non-https `link` must not shadow a usable `verificationLink`.
  const candidates = [ksef?.link, ksef?.verificationLink];

  return candidates.find((candidate): candidate is string => Boolean(candidate?.startsWith("https://"))) ?? null;
};

const formatKsefStatus = (status: string | null | undefined, t: TFunction): string => {
  const statusKey = status ?? "not_sent";
  // Fall back to the raw status so unknown values still render something readable.
  return t(`invoices.ksef.status.${statusKey}`, statusKey);
};

const formatErrorMessage = (errorMessage: unknown): string => {
  if (typeof errorMessage === "string") {
    return errorMessage;
  }
  if (typeof errorMessage === "object" && errorMessage !== null) {
    // Handle object with arrays of error messages like { number: ["..."], positions: [...] }
    const messages: string[] = [];
    for (const [key, value] of Object.entries(errorMessage)) {
      if (Array.isArray(value)) {
        const stringMessages = value.filter((v): v is string => typeof v === "string");
        if (stringMessages.length > 0) {
          messages.push(`${key}: ${stringMessages.join(", ")}`);
        }
      }
    }
    if (messages.length > 0) {
      return messages.join("; ");
    }
  }
  return JSON.stringify(errorMessage);
};

const InvoiceNumberInfo: FunctionComponent<Props> = ({
  invoice,
  withId = false,
  hideError = false,
  orderId,
  productId,
  onKsefRefreshed,
}) => {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const [refreshing, setRefreshing] = useState(false);
  // Rendered in place of the row's stored metadata so the badge reflects the refresh
  // even when the caller does not reload the table.
  const [refreshedKsef, setRefreshedKsef] = useState<InvoiceKsefMetadata | null>(null);

  const onRefreshKsef = async () => {
    if (!orderId || !productId) {
      return;
    }

    setRefreshing(true);

    const result = await dispatch(productClientActions.refreshKsefStatus({ orderId, productId }));

    setRefreshing(false);

    if (productClientActions.refreshKsefStatus.rejected.match(result)) {
      void message.error(t("invoices.ksef.refreshError"));

      return;
    }

    setRefreshedKsef(result.payload.ksef);
    void message.success(
      t("invoices.ksef.refreshed", {
        status: formatKsefStatus(result.payload.ksef?.status, t),
      }),
    );
    onKsefRefreshed?.();
  };

  if (!invoice) {
    return <span>-</span>;
  }

  if ("errorMessage" in invoice) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const parsedError = invoice.errorData
      ? typeof invoice.errorData === "string"
        ? JSON.parse(invoice.errorData)
        : invoice.errorData?.message
      : null;

    const formattedErrorMessage = formatErrorMessage(invoice.errorMessage);
    // eslint-disable-next-line
    const formattedErrorData = parsedError
      ? typeof parsedError === "string"
        ? parsedError
        : // eslint-disable-next-line
          parsedError?.message || JSON.stringify(parsedError)
      : null;

    const tooltipTitle = hideError
      ? ""
      : formattedErrorData
        ? `${formattedErrorMessage} - ${formattedErrorData}`
        : formattedErrorMessage;

    return (
      <Tooltip title={tooltipTitle}>
        <Space>
          <Badge status="error" text={t("invoices.errorWhenIssuing")} />
          {!hideError && <InfoCircleOutlined />}
        </Space>
      </Tooltip>
    );
  }
  const ksef = refreshedKsef ?? invoice.ksef;
  // A document that never reached KSeF has nothing to re-read, so the refresh would
  // always be a no-op — only offer it once KSeF actually reported a status.
  const canRefreshKsef = Boolean(ksef?.status && orderId && productId);
  const ksefLink = getKsefLink(ksef);

  return (
    <Space size="small">
      <span>
        {t("number")}: {invoice.number} {withId ? ` / ID: ${invoice.id} (${t(`invoices.${invoice.client}`)})` : ""}
      </span>
      {ksef ? (
        <Tooltip
          title={
            // color: inherit keeps the tooltip's own light text — Typography.Text would
            // otherwise apply its dark body colour on the dark tooltip background.
            <Space direction="vertical" size={0}>
              <Typography.Text style={{ color: "inherit" }}>{formatKsefStatus(ksef.status, t)}</Typography.Text>
              {ksef.govId ? (
                <Typography.Text style={{ color: "inherit" }}>
                  {t("invoices.ksef.number")}: {ksef.govId}
                </Typography.Text>
              ) : null}
              {ksef.errors?.length ? (
                <Typography.Text style={{ color: "inherit" }}>{ksef.errors.join(", ")}</Typography.Text>
              ) : null}
            </Space>
          }
        >
          <Badge
            status={getKsefBadgeStatus(ksef.status)}
            text={`${t("invoices.ksef.label")}: ${formatKsefStatus(ksef.status, t)}`}
          />
        </Tooltip>
      ) : null}
      {ksefLink ? (
        <Tooltip title={t("invoices.ksef.openInKsef")}>
          <Button
            type="text"
            size="small"
            aria-label={t("invoices.ksef.openInKsef")}
            icon={<LinkOutlined />}
            href={ksefLink}
            target="_blank"
            rel="noopener noreferrer"
          />
        </Tooltip>
      ) : null}
      {canRefreshKsef ? (
        <Tooltip title={t("invoices.ksef.refresh")}>
          <Button
            type="text"
            size="small"
            aria-label={t("invoices.ksef.refresh")}
            loading={refreshing}
            icon={<ReloadOutlined />}
            onClick={() => void onRefreshKsef()}
          />
        </Tooltip>
      ) : null}
    </Space>
  );
};

export default InvoiceNumberInfo;
